import React, { useMemo, useState, useCallback } from "react";
import { Input } from "reactstrap";

/**
 * DNA list — UI v2.
 *
 * Replaces the dropdown with a scrollable, clickable list, matching the
 * legacy listBoxDna pane: every DNA visible at once, one tap to switch,
 * fail markers readable at a glance.
 *
 * Contract unchanged: `data` (op-filtered MAFIA rows), `selectedDna`,
 * `onSelectDna(row|null)`, `failedDnas` (Set of DNA numbers with failing
 * entries — Batch 2 / Round 9, legacy "!" markers). The fail marker is
 * DISPLAY-ONLY: selection objects keep the clean DNA number so every
 * backend lookup stays unchanged.
 */
const DnaNumDetails = ({ data = [], selectedDna, onSelectDna, failedDnas }) => {
  const [search, setSearch] = useState("");

  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const options = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return safeData;
    return safeData.filter((d) => {
      const dna = String(d?.DNANum ?? "").toLowerCase();
      // real column is SpecialGages ("Gage to use"); Tool kept as fallback
      const tool = String(d?.SpecialGages ?? d?.Tool ?? "").toLowerCase();
      const dim = String(d?.DimType ?? "").toLowerCase();
      return dna.includes(s) || tool.includes(s) || dim.includes(s);
    });
  }, [safeData, search]);

  const selectedValue = useMemo(
    () => (selectedDna?.DNANum != null ? String(selectedDna.DNANum).trim() : ""),
    [selectedDna]
  );

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  return (
    <div className="di-panel" style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
      <div className="di-panel__head">
        <span>DNA</span>
        <span>
          {options.length}/{safeData.length}
        </span>
      </div>
      <div className="di-panel__body" style={{ paddingBottom: 6 }}>
        <Input
          bsSize="sm"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search DNA / tool / dim type..."
        />
      </div>
      {options.length === 0 ? (
        <div className="text-muted small px-3 pb-3">No DNAs match.</div>
      ) : (
        <ul className="di-dna-list">
          {options.map((item, idx) => {
            const dna = String(item?.DNANum ?? "").trim();
            const tool = String(item?.SpecialGages ?? item?.Tool ?? "").trim();
            const failed = failedDnas?.has?.(dna);
            const active = dna !== "" && dna === selectedValue;
            return (
              <li key={`${dna || idx}`}>
                <button
                  type="button"
                  className={[
                    "di-dna-row",
                    active ? "di-dna-row--active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onSelectDna?.(item)}
                  title={
                    failed
                      ? "This DNA has a failing entry (legacy \"!\" marker)"
                      : undefined
                  }
                >
                  <span style={{ minWidth: 0 }}>
                    <span className="di-dna-row__num">{dna || "—"}</span>
                    {tool ? (
                      <span className="di-dna-row__tool d-block">{tool}</span>
                    ) : null}
                  </span>
                  {failed ? <span className="di-dna-fail">!</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default React.memo(DnaNumDetails);
